import type { Course, AppSettings } from '@gpa-scales/shared-types';
import { CourseType, ResidencyStatus, AGSubject, ValidationSeverity } from '@gpa-scales/shared-types';
import { generateId } from '../utils/id';

export interface EdgeCase {
  id: string;
  severity: ValidationSeverity;
  courseId?: string;
  type: string;
  message: string;
  suggestion?: string;
}

export function detectEdgeCases(
  courses: Course[],
  settings: AppSettings
): EdgeCase[] {
  const cases: EdgeCase[] = [];

  for (const course of courses) {
    // Out-of-state honors
    if (
      settings.residencyStatus === ResidencyStatus.NonResident &&
      (course.courseType === CourseType.Honors || course.courseType === CourseType.DualEnrollment)
    ) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Warning,
        courseId: course.id,
        type: 'out_of_state_honors',
        message: `"${course.name}": Out-of-state honors/Dual Enrollment courses don't qualify for UC bonus points.`,
        suggestion: 'Only AP and IB courses qualify for non-resident honors bonus.',
      });
    }

    // College courses
    if (course.courseType === CourseType.College) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        type: 'college_course',
        message: `"${course.name}": College-level course detected. Verify grade conversion is correct.`,
        suggestion: 'College courses may use a different grading scale. Ensure the grade format is set correctly.',
      });
    }

    // Unsupported symbols in grade
    if (course.grade && !course.specialGrade) {
      const unsupportedMatch = course.grade.match(/[^A-Fa-f0-9.+\-%\s]/);
      if (unsupportedMatch) {
        cases.push({
          id: generateId(),
          severity: ValidationSeverity.Warning,
          courseId: course.id,
          type: 'unsupported_symbol',
          message: `"${course.name}": Grade "${course.grade}" contains unusual characters.`,
          suggestion: 'Verify the grade is entered correctly.',
        });
      }
    }

    // A-G maybe (course type is advanced but no A-G assigned)
    if (
      (course.courseType === CourseType.AP || course.courseType === CourseType.IB_HL || course.courseType === CourseType.IB_SL) &&
      course.agSubject === AGSubject.None
    ) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        type: 'ag_maybe',
        message: `"${course.name}": Advanced course (${course.courseType.toUpperCase()}) has no A-G subject assigned.`,
        suggestion: 'Most AP/IB courses qualify for an A-G category. Assign one if applicable.',
      });
    }

    // Credit outliers
    if (course.credits > 2.0) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        type: 'credit_outlier',
        message: `"${course.name}": ${course.credits} credits is unusually high for a single course.`,
        suggestion: 'Verify the credit value is correct.',
      });
    }

    if (course.credits < 0.5 && course.credits > 0) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        type: 'credit_outlier',
        message: `"${course.name}": ${course.credits} credits is unusually low.`,
        suggestion: 'Most courses are worth at least 0.5 credits.',
      });
    }

    // Suspicious grade patterns: A in AP but F in regular
    if (
      (course.courseType === CourseType.AP || course.courseType === CourseType.IB_HL) &&
      (course.grade === 'F' || course.grade === 'D' || course.grade === 'D-' || course.grade === 'D+')
    ) {
      cases.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        type: 'suspicious_grade',
        message: `"${course.name}": Low grade (${course.grade}) in an advanced course.`,
        suggestion: 'Verify this grade is correct. Consider the impact on weighted GPA.',
      });
    }
  }

  return cases;
}
